import React, { useEffect, useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Link } from 'react-router-dom';
import { Cloud, Info, ExternalLink } from 'lucide-react';
import { getBackupPrefs, setBackupPrefs } from '@/lib/mediaLibrary';

/**
 * Automatic backup preferences (section 14) — configuration only. No
 * background job runs from this screen in Levels 1–2; actual scheduled
 * transfers, retries and verification are Level 3. Saving a preference
 * here never performs a backup.
 */
export function BackupSettingsPanel({ creatorId, projects, dropboxConnected }) {
  const [prefs, setPrefs] = useState(() => getBackupPrefs(creatorId));
  useEffect(() => { setPrefs(getBackupPrefs(creatorId)); }, [creatorId]);

  const update = (patch) => setPrefs(setBackupPrefs(creatorId, patch));
  const toggleProject = (id) => {
    const ids = prefs.scopeProjectIds.includes(id) ? prefs.scopeProjectIds.filter((p) => p !== id) : [...prefs.scopeProjectIds, id];
    update({ scopeProjectIds: ids });
  };

  return (
    <div className="space-y-5 max-w-xl" data-testid="backup-settings-panel">
      <div className="flex items-center justify-between bg-white/[0.03] border border-white/5 rounded-lg px-4 py-3">
        <div className="flex items-center gap-2">
          <Cloud className="h-4 w-4 text-[#3A7BFF]" />
          <div>
            <p className="text-sm text-white">Dropbox connection</p>
            <p className="text-[11px] text-[#555E6E]">Backblaze B2 is your primary storage; Dropbox is only an optional extra copy.</p>
          </div>
        </div>
        <Badge variant="outline" className={dropboxConnected ? 'border-green-500/30 text-green-400' : 'border-yellow-500/30 text-yellow-400'}>{dropboxConnected ? 'Connected' : 'Not connected'}</Badge>
      </div>
      {!dropboxConnected && (
        <Link to="/app/account/creator-profile" className="text-xs text-[#3A7BFF] hover:text-white flex items-center gap-1" data-testid="backup-settings-connect-link">Connect Dropbox in Creator Profile<ExternalLink className="h-3 w-3" /></Link>
      )}

      <div className="flex items-center justify-between">
        <Label className="text-sm text-white">Automatic backup</Label>
        <Switch checked={prefs.autoEnabled} onCheckedChange={(v) => update({ autoEnabled: v })} data-testid="backup-auto-enabled-switch" />
      </div>

      {prefs.autoEnabled && (
        <>
          <div>
            <Label className="text-xs text-[#8A93A6]">Frequency</Label>
            <Select value={prefs.frequency} onValueChange={(v) => update({ frequency: v })}>
              <SelectTrigger className="h-8 text-xs bg-black/40 border-white/10 mt-1 w-48" data-testid="backup-frequency-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1C1F26] border-white/10">
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-[#8A93A6] mb-1.5 block">Backup scope — which projects</Label>
            <div className="space-y-1.5">
              {projects.map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-xs text-[#C0C7D4] cursor-pointer">
                  <Checkbox checked={prefs.scopeProjectIds.includes(p.id)} onCheckedChange={() => toggleProject(p.id)} />{p.name}
                </label>
              ))}
              {projects.length === 0 && <p className="text-xs text-[#555E6E]">No projects yet.</p>}
            </div>
          </div>
        </>
      )}

      <div className="grid grid-cols-2 gap-3 text-xs">
        <div className="bg-white/[0.03] border border-white/5 rounded-lg p-3"><p className="text-[#555E6E]">Last confirmed backup</p><p className="text-white mt-1">Never</p></div>
        <div className="bg-white/[0.03] border border-white/5 rounded-lg p-3"><p className="text-[#555E6E]">Backup errors</p><p className="text-white mt-1">None</p></div>
      </div>

      <div className="flex items-start gap-2 bg-white/[0.03] border border-white/5 rounded-lg p-3">
        <Info className="h-3.5 w-3.5 text-[#8A93A6] shrink-0 mt-0.5" />
        <p className="text-[11px] text-[#8A93A6]">These preferences are saved, but no automatic transfer runs yet in this build — scheduled backups, retries and backup history are Level 3. Use "Back Up to Dropbox" on a project or folder for the manual flow.</p>
      </div>
    </div>
  );
}
